import React, { useCallback, useState } from 'react'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { serverMessage } from '../../../../shared/sso-workspace/format'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { disconnect, reconnect } from './alert-routing-api'
import { providerLabel, useLeaveForConsent } from './connection-field'
import { connectionHref } from './paths'

// What Disconnect stops, in a sentence that names every target.
export const disconnectText = (connection, label) => {
  const targets = connection.targets || []
  if (!targets.length) return 'No target uses this workspace. You can connect it again at any time.'
  const names = targets.map(t => t.name).join(', ')
  const lead = targets.length === 1 ? 'This target will stop' : `These ${targets.length} targets will stop`
  return `${lead} until ${label} is reconnected: ${names}. ${targets.length === 1 ? 'It stays' : 'They stay'}, and alerts for ${targets.length === 1 ? 'it' : 'them'} are held.`
}

// What EducationXR may do in the workspace, in words: the scopes the
// workspace granted, grouped. The codes are only the hover (owner
// 2026-10-30: "raw scope codes"). Creating channels is the server's own
// answer (canCreateChannels: every scope it needs); a scope this list doesn't
// know is one "Other Slack permissions" line.
export const ABILITIES = [
  { key: 'post', label: 'Post alerts to channels', scopes: ['chat:write', 'chat:write.public'] },
  { key: 'list', label: 'See the channel list', scopes: ['channels:read', 'groups:read'] },
  { key: 'create', label: 'Create channels', scopes: ['channels:manage', 'groups:write'] }
]
const KNOWN = new Set(ABILITIES.flatMap(a => a.scopes))
// An ability not allowed yet whose scopes wait for approval (missingScopes)
// says so; the waiting scopes no ability names are one count (unknownMissing).
export const abilitiesOf = connection => {
  const granted = connection.scopes || []
  const missing = Array.isArray(connection.missingScopes) ? connection.missingScopes : []
  const rows = ABILITIES.map(a => {
    const allowed = a.key === 'create' ? Boolean(connection.canCreateChannels) : a.scopes.some(s => granted.includes(s))
    return { key: a.key, label: a.label, codes: a.scopes.join(', '), allowed, waiting: !allowed && a.scopes.some(s => missing.includes(s)) }
  })
  const other = granted.filter(s => !KNOWN.has(s))
  return other.length ? [...rows, { key: 'other', label: 'Other Slack permissions', codes: other.join(', '), allowed: true }] : rows
}
export const unknownMissingOf = connection => (Array.isArray(connection.missingScopes) ? connection.missingScopes.filter(s => !KNOWN.has(s)) : [])

// A connection's Reconnect and Disconnect, the same from its row and from its
// drawer. Reconnect leaves for the provider's consent page and comes back to
// the connection's drawer. Disconnect asks first, naming every target it
// stops (ConfirmDialog with the scrim); onDisconnected gets the answer.
export const useConnectionActions = ({ onDisconnected } = {}) => {
  const consent = useLeaveForConsent()
  const [asking, setAsking] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()

  const startReconnect = useCallback(connection => consent.leave(() => reconnect(connection.id),
    { provider: connection.provider, purpose: 'reconnect', returnTo: connectionHref(connection.id) },
    `${providerLabel(connection.provider)} couldn't be reached. Try again.`), [consent])

  const askDisconnect = useCallback(connection => { setError(null); setAsking(connection) }, [setError])

  const confirm = async () => {
    if (busy || !asking) return
    setBusy(true)
    setError(null)
    try {
      const answer = await disconnect(asking.id)
      const gone = asking
      setAsking(null)
      if (onDisconnected) onDisconnected(answer, gone)
    } catch (err) {
      setError(serverMessage(err, "It couldn't be disconnected."), err)
    } finally {
      setBusy(false)
    }
  }

  const dialogs = (
    <ConfirmDialog open={Boolean(asking)} scrim title={asking ? `Disconnect ${asking.externalName}?` : ''} confirmText="Disconnect" busy={busy}
      body={asking ? disconnectText(asking, providerLabel(asking.provider)) : ''} error={error} failure={failure}
      onCancel={() => setAsking(null)} onConfirm={confirm} />
  )

  return { reconnect: startReconnect, askDisconnect, consent, busy, dialogs }
}
